import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Fade,
  LinearProgress,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  ArrowForward,
  ErrorOutline,
  HomeOutlined,
  MailOutline,
  SecurityOutlined,
  TaskAltOutlined,
} from "@mui/icons-material";
import SurfaceCard from "../../components/common/ui/SurfaceCard";
import { useVerifyEmailChange } from "../../hooks/user";
import {
  PROFILE_TAB_QUERY_PARAM,
  PROFILE_TAB_SECURITY,
  USER_PROFILE_ROUTE_PATH,
} from "../../utils/constants/AppConstants";
import { ApiError } from "../../error/errorTypes";

type VerifyType = "old" | "new";

type VerificationStatus = {
  message: string;
  oldEmailVerified: boolean;
  newEmailVerified: boolean;
  changeCompleted: boolean;
};

const VerifyEmailChangePage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const token = useMemo(() => params.get("token") || "", [params]);
  const verifyType = useMemo<VerifyType | null>(() => {
    const t = (params.get("type") || "").toLowerCase();
    if (t === "old" || t === "new") return t;
    return null;
  }, [params]);

  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<VerificationStatus | null>(null);
  const [redirectCountdown, setRedirectCountdown] = useState<number | null>(null);

  const { mutate: verifyEmailChange, isPending: loading } = useVerifyEmailChange();

  useEffect(() => {
    if (!token || !verifyType) {
      setError("Invalid verification link. The token or verification type is missing.");
      return;
    }

    verifyEmailChange(
      { token, type: verifyType },
      {
        onSuccess: (result) => {
          if (!result) {
            setError("Verification could not be completed. Please try again.");
            return;
          }
          setStatus(result);
        },
        onError: (err) => {
          if (err instanceof ApiError) {
            setError(err.userMessage || err.message || "Verification failed. The link may be invalid or expired.");
            return;
          }
          if (err instanceof Error) {
            setError(err.message);
            return;
          }
          setError("Verification failed. The link may be invalid or expired.");
        },
      }
    );
  }, [token, verifyType, verifyEmailChange]);

  useEffect(() => {
    if (!status) return;

    setRedirectCountdown(4);
    const interval = setInterval(() => {
      setRedirectCountdown((prev) => {
        if (prev === null) return null;
        if (prev <= 1) {
          clearInterval(interval);
          navigate(`/${USER_PROFILE_ROUTE_PATH}?${PROFILE_TAB_QUERY_PARAM}=${PROFILE_TAB_SECURITY}`);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [status, navigate]);

  const goToSecuritySettings = () => {
    navigate(`/${USER_PROFILE_ROUTE_PATH}?${PROFILE_TAB_QUERY_PARAM}=${PROFILE_TAB_SECURITY}`);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        py: 4,
        background: `radial-gradient(circle at 10% 10%, ${alpha(theme.palette.primary.main, 0.16)} 0%, transparent 45%), radial-gradient(circle at 90% 20%, ${alpha(theme.palette.info.main, 0.12)} 0%, transparent 45%), linear-gradient(180deg, ${theme.palette.background.default} 0%, ${alpha(theme.palette.background.paper, 0.7)} 100%)`,
      }}
    >
      <Container maxWidth="sm">
        <Fade in timeout={500}>
          <SurfaceCard
            sx={{
              borderRadius: "16px",
              border: `1px solid ${alpha(theme.palette.primary.main, 0.24)}`,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                p: 2.4,
                borderBottom: `1px solid ${theme.palette.divider}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.16)} 0%, ${alpha(theme.palette.background.paper, 0.35)} 100%)`,
              }}
            >
              <Stack direction="row" spacing={1.2} alignItems="center">
                <Avatar
                  sx={{
                    width: 38,
                    height: 38,
                    bgcolor: alpha(theme.palette.primary.main, 0.22),
                    color: theme.palette.primary.light,
                  }}
                >
                  <SecurityOutlined fontSize="small" />
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Verify Email Change
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Confirm your email ownership to secure the account change.
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Box sx={{ p: { xs: 2.2, md: 3 } }}>
              {loading && (
                <Stack spacing={2} alignItems="center" sx={{ textAlign: "center", py: 2 }}>
                  <CircularProgress size={34} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Verifying your link
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    This usually takes a moment.
                  </Typography>
                </Stack>
              )}

              {!loading && error && (
                <Stack spacing={2}>
                  <Alert severity="error" icon={<ErrorOutline />}>
                    {error}
                  </Alert>
                  <Typography variant="body2" color="text.secondary">
                    The verification link may be expired, already used, or malformed. You can start again from Security settings.
                  </Typography>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                    <Button variant="contained" onClick={goToSecuritySettings} endIcon={<ArrowForward />}>
                      Go to Security
                    </Button>
                    <Button variant="outlined" onClick={() => navigate("/")} startIcon={<HomeOutlined />}>
                      Back to Home
                    </Button>
                  </Stack>
                </Stack>
              )}

              {!loading && !error && status && (
                <Stack spacing={2.1}>
                  <Stack spacing={1} alignItems="center" sx={{ textAlign: "center" }}>
                    <TaskAltOutlined sx={{ color: theme.palette.success.main, fontSize: 48 }} />
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      Email Verified
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {verifyType === "old"
                        ? "Your current email is verified. Please verify your new email from the link we sent."
                        : "Your new email is verified. Return to Security to complete the email update."}
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      p: 1.2,
                      borderRadius: "10px",
                      border: `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
                      background: alpha(theme.palette.background.default, 0.32),
                    }}
                  >
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                      <Chip
                        size="small"
                        icon={<MailOutline fontSize="small" />}
                        label={status.oldEmailVerified ? "Current email verified" : "Current email pending"}
                        color={status.oldEmailVerified ? "success" : "default"}
                        variant={status.oldEmailVerified ? "filled" : "outlined"}
                      />
                      <Chip
                        size="small"
                        icon={<MailOutline fontSize="small" />}
                        label={status.newEmailVerified ? "New email verified" : "New email pending"}
                        color={status.newEmailVerified ? "success" : "default"}
                        variant={status.newEmailVerified ? "filled" : "outlined"}
                      />
                    </Stack>
                  </Box>

                  {redirectCountdown !== null && (
                    <Box
                      sx={{
                        p: 1.2,
                        borderRadius: "10px",
                        border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                        background: alpha(theme.palette.info.main, 0.1),
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.8 }}>
                        Redirecting to Security settings in {redirectCountdown}s...
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={((4 - Math.max(redirectCountdown, 0)) / 4) * 100}
                        sx={{
                          height: 6,
                          borderRadius: 999,
                          backgroundColor: alpha(theme.palette.info.main, 0.2),
                          "& .MuiLinearProgress-bar": {
                            borderRadius: 999,
                          },
                        }}
                      />
                    </Box>
                  )}

                  <Button variant="contained" onClick={goToSecuritySettings} endIcon={<ArrowForward />}>
                    Go to Security now
                  </Button>
                </Stack>
              )}
            </Box>
          </SurfaceCard>
        </Fade>
      </Container>
    </Box>
  );
};

export default VerifyEmailChangePage;
